import React, { useEffect, useState } from "react";
import { Modal, Button, Row, Col, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { companyID } from "../auth";
import { calculateSalaryBreakdown } from "../utils/calculateSalaryBreakdown";
import { roundoffAmount } from "../utils/roundoffAmount";
import { Table } from "react-bootstrap";
import { convertDateFormate } from "../utils/convertDateFormate";
import { Spin } from "antd";
import { formatDecimal } from "../utils/formatDecimal";
import api from "../api/axios";
import { getDaysInMonth } from "../utils/getDaysInMonth";
import { getPerformanceBonusReleaseDate } from "../services/performanceBonusServices";

const SalaryDetail = ({
  employeeId,
  show,
  handleClose,
  handleGenerate,
  loading,
  setLoading,
  salaryId,
  salaryData,
}) => {
  const [formData, setFormData] = useState({
    other_deduction: 0,
    provident_fund: 0,
    gratuity: 0,
    specialAllowance: 0,
    mobileInternetAllowance: 0,
    medicalInsurance: 0,
    performance_bonus: 0,
    net_salary: 0,
    totalPay: 0,
    total_deduction: 0,
    other_deduction_reason: "",
    released_performance_bonus: 0,
  });

  const currentDate = new Date();

  const getMaxDate = () => {
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    let maxDate = `${currentYear}-${
      currentMonth < 10 ? "0" + currentMonth : currentMonth
    }`;
    return maxDate;
  };

  const [monthYear, setMonthYear] = useState(getMaxDate());

  const [companyWorkingDays, setCompanyWorkingDays] = useState(null);
  const [employeeWorkingDetail, setEmployeeWorkingDetail] = useState(null);
  const [currentSalary, setCurrentSalary] = useState(null);
  const [companySalaryStructure, setCompanySalaryStructure] = useState(null);
  const [companySalaryConfig, setCompanySalaryConfig] = useState(null);
  const [error, setError] = useState({});
  const [inEligibilityReason, setInEligibilityReason] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const totalDeduction =
      Number(formData.performance_bonus || 0) +
      Number(formData.provident_fund || 0) +
      Number(formData.gratuity || 0) +
      Number(value || 0);

    if (value > formData.net_salary) {
      setError((prevData) => ({
        ...prevData,
        [name]: "The deduction amount exceeds the salary.",
        other_deduction_reason: "",
      }));
      setFormData({
        ...formData,
        [name]: value,
        total_deduction: totalDeduction,
      });
      return;
    }
    setFormData({
      ...formData,
      [name]: value,
      total_deduction: roundoffAmount(totalDeduction),
    });
    setError((prevData) => ({
      ...prevData,
      [name]: null,
      other_deduction_reason: "",
    }));
  };

  const handleDeductionReason = (e) => {
    const { name, value } = e.target;
    if (!value.trim()) {
      setError((prevData) => ({
        ...prevData,
        [name]: "Please mention deduction reason.",
      }));
      setFormData({ ...formData, [name]: value });
    } else {
      setError((prevData) => ({ ...prevData, [name]: null }));
      setFormData({ ...formData, [name]: value });
    }
  };

  const company_id = companyID();

  const fetchCompanySalaryConfig = async () => {
    try {
      const response = await api.get(`salary-config/${company_id}`);
      setCompanySalaryConfig(response.data.config);
      return response.data.config;
    } catch (err) {
      console.error("Error fetching salary config:", err);
    }
  };

  useEffect(() => {
    if (company_id) {
      fetchCompanySalaryConfig();
    }
  }, [company_id]);

  const calculateSalaryGenerationDate = React.useMemo(() => {
    if (!companySalaryConfig) return 0;
    const { payroll_release_date, payroll_processing_days } =
      companySalaryConfig;
    return payroll_release_date - payroll_processing_days;
  }, [companySalaryConfig]);

  const checkCorrectionPending = async (
    e,
    year,
    month,
    maxDate,
    changeDate
  ) => {
    const selectedDate = e.target.value;
    // const paddedMonth = month < 10 ? `0${month}` : `${month}`;

    const isCorrectionPending =
      companySalaryConfig?.payroll_release_month !== "same_month"
        ? (selectedDate === `${year}-${month - 1}` &&
            currentDate.getDate() < calculateSalaryGenerationDate) ||
          selectedDate === maxDate
        : currentDate.getDate() <= calculateSalaryGenerationDate &&
          selectedDate === maxDate;

    changeDate(selectedDate);
    setError((prev) => ({ ...prev, correctionPending: isCorrectionPending }));
  };

  const handleMonthYearChange = (e) => {
    const changeDate = (date) => {
      setMonthYear(date);
      setFormData((prevData) => ({
        ...prevData,
        other_deduction: 0,
        total_deduction: 0,
        other_deduction_reason: "",
      }));
      setError({});
      const inputField = document.getElementById("other_deduction");
      if (inputField) {
        inputField.value = "";
      }
      const deductionReasonInputField =
        document.getElementById("deduction_reason");
      if (deductionReasonInputField) {
        deductionReasonInputField.value = "";
      }
    };
    const maxDate = getMaxDate();
    const { year, month } = convertDateFormate(maxDate);
    checkCorrectionPending(e, year, month, maxDate, changeDate);
  };

  const selectedMonthYear = convertDateFormate(monthYear);

  const fetchCompanyWorkingDays = () => {
    setLoading(true);
    api
      .get(
        `working-days/${company_id}/${selectedMonthYear.month}/${selectedMonthYear.year}?employee_id=${employeeId}`
      )
      .then((res) => {
        setCompanyWorkingDays(res.data.company_working_days);
        fetchEmployeementData(res.data.employee_working_days);
      })
      .catch((err) => {
        console.error(err);
        setInEligibilityReason(err.response?.data?.message ?? "");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchEmployeementData = (companyWorkingDays) => {
    api
      .get(
        `employees/companies/${company_id}/employees/${employeeId}/salary-breakdown`,
        {
          params: { month: monthYear },
        }
      )
      .then((res) => {
        setCurrentSalary(res.data.employmentData.salary);
        setCompanySalaryStructure(res.data.config);
        fetchEmployeeWorkingDetail(
          res.data.employmentData,
          res.data.config,
          companyWorkingDays
        );
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        // loader
      });
  };

  const fetchEmployeeWorkingDetail = (
    employmentData,
    companySalaryStructure,
    companyWorkingDays
  ) => {
    api
      .post(`calculate-salary`, {
        employee_id: employeeId,
        company_id: company_id,
        month: selectedMonthYear.month,
        year: selectedMonthYear.year,
      })
      .then((res) => {
        setInEligibilityReason("");
        setEmployeeWorkingDetail(res.data.salaryDetails[0]);
        const per_day_salary =
          employmentData.salary / getDaysInMonth(monthYear);
        const current_month_salary =
          (res.data.salaryDetails[0]?.salary_eligible_days +
            companyWorkingDays.weekOffDatesCount) *
          per_day_salary;
        // const current_month_salary =
        //   employmentData.salary - deductSalaryForAbsent;
        const roundedSalary = roundoffAmount(current_month_salary);
        const performance_bonus_amount = roundoffAmount(
          (employmentData.performance_bonus * employmentData.salary) / 100
        );
        const {
          basic,
          hra,
          medicalAllowance,
          statutoryBonus,
          otherAllowance,
          totalPay,
          pfContribution,
          gratuity,
          specialAllowance,
          mobileInternetAllowance,
          medicalInsurance,
        } = calculateSalaryBreakdown(roundedSalary, companySalaryStructure);

        // Calculate total deduction properly
        const calculatedTotalDeduction = roundoffAmount(
          Number(performance_bonus_amount || 0) +
            Number(pfContribution || 0) +
            Number(gratuity || 0) +
            Number(employmentData.other_deduction || 0)
        );

        setFormData((prevData) => ({
          ...prevData,
          ...{
            basic,
            hra,
            medicalAllowance,
            statutoryBonus,
            otherAllowance,
            totalPay,
            provident_fund: pfContribution,
            gratuity: gratuity,
            specialAllowance: specialAllowance,
            mobileInternetAllowance: mobileInternetAllowance,
            medicalInsurance: medicalInsurance,
          },
          net_salary: roundoffAmount(
            Number(totalPay) -
              performance_bonus_amount -
              (employmentData?.provident_fund || pfContribution || 0) -
              (employmentData?.other_deduction || 0) +
              Number(roundoffAmount(employmentData.total_reimbursement)) +
              prevData.released_performance_bonus
          ),
          total_deduction: calculatedTotalDeduction,
          ...res.data.salaryDetails[0],
          performance_bonus: Number(performance_bonus_amount),
          employee_salary: Number(employmentData.salary),
          total_reimbursement: Number(
            roundoffAmount(employmentData.total_reimbursement)
          ),
          totalPay:
            Number(totalPay) +
            Number(roundoffAmount(employmentData.total_reimbursement)) +
            prevData.released_performance_bonus,
        }));
      })
      .catch((err) => {
        setInEligibilityReason(err?.response?.data?.message ?? "");
      })
      .finally(() => {
        // loader
      });
  };

  useEffect(() => {
    if (salaryId) {
      setLoading(false);
      setFormData(salaryData);
      setError((prevData) => ({ ...prevData, correctionPending: false }));
      setMonthYear(salaryData.month);
    } else if (employeeId && monthYear) {
      fetchCompanyWorkingDays();
    } else {
      //changes
      const maxDate = getMaxDate();
      setMonthYear(maxDate);
      console.log(
        companySalaryConfig?.payroll_release_month === "same_month" &&
          currentDate.getDate() >= calculateSalaryGenerationDate
      );
      if (
        companySalaryConfig?.payroll_release_month === "same_month" &&
        currentDate.getDate() >= calculateSalaryGenerationDate
      ) {
        setError((prevData) => ({
          ...prevData,
          correctionPending: false,
        }));
      } else {
        setError((prevData) => ({
          ...prevData,
          correctionPending: true,
        }));
      }

      // setError({})
      setFormData({
        other_deduction: 0,
        provident_fund: 0,
        performance_bonus: 0,
        net_salary: 0,
        totalPay: 0,
        total_deduction: 0,
        other_deduction_reason: "",
      });
      const inputField = document.getElementById("other_deduction");
      if (inputField) {
        inputField.value = "";
      }
      const deductionReasonInputField =
        document.getElementById("deduction_reason");
      if (deductionReasonInputField) {
        deductionReasonInputField.value = "";
      }
    }
  }, [show, employeeId, monthYear, companySalaryConfig]);

  const companyThemeColor = localStorage.getItem("companyColor");

  const handleGenerateButton = () => {
    const salaryData = {
      ...formData,
      month: selectedMonthYear.month,
      employee_id: employeeId,
      company_id,
      total_working_days:
        getDaysInMonth(monthYear) - companyWorkingDays?.weekOffDatesCount,
      ...employeeWorkingDetail,
      currentMonthSalary: formData.totalPay,
      slipMonth:
        new Date(
          selectedMonthYear.year,
          selectedMonthYear.month - 1
        ).toLocaleString("default", { month: "short" }) +
        " " +
        selectedMonthYear.year,
      salaryMonth: monthYear,
      net_salary: roundoffAmount(
        formData?.net_salary - roundoffAmount(formData?.other_deduction)
      ),
      other_deduction: roundoffAmount(formData.other_deduction),
      employee_salary: Number(formData.employee_salary),
      other_deduction_reason: Number(formData.other_deduction)
        ? formData.other_deduction_reason
        : null,
      reimbursement_allowance: formData.total_reimbursement || 0,
      released_performance_bonus: formData.released_performance_bonus ?? 0,
      bonus_release_id: formData.bonus_release_id || null,
      provident_fund: formData.provident_fund || 0,
      gratuity: formData.gratuity || 0,
      special_allowance: formData.specialAllowance || 0,
      mobile_internet_allowance: formData.mobileInternetAllowance || 0,
      medical_insurance: formData.medicalInsurance || 0,
    };
    handleGenerate(salaryData);
  };

  const handleDisableGenerateButton = () => {
    return inEligibilityReason ||
      error.other_deduction ||
      (formData.other_deduction
        ? !formData.other_deduction_reason?.trim()
        : error.other_deduction_reason)
      ? true
      : false;
  };

  useEffect(() => {
    if (employeeId && monthYear) {
      getPerformanceBonusReleaseDate(monthYear, employeeId)
        .then((res) => {
          if (res?.releaseData?.length > 0) {
            setFormData((prevData) => ({
              ...prevData,
              bonus_release_id: res.releaseData[0].id,
              released_performance_bonus: roundoffAmount(
                res.releaseData[0].released_amount
              ),
              net_salary: roundoffAmount(
                prevData.net_salary + res.releaseData[0].released_amount
              ),
            }));
          } else {
            setFormData((prevData) => ({
              ...prevData,
              released_performance_bonus: 0,
              bonus_release_id: null,
              net_salary: roundoffAmount(
                prevData.net_salary - prevData.released_performance_bonus
              ),
              totalPay: roundoffAmount(
                prevData.totalPay - prevData.released_performance_bonus
              ),
            }));
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [monthYear, employeeId]);

  // Separate earnings and deductions fields
  const earningsFields = [
    { key: "basic", label: "Basic" },
    { key: "hra", label: "HRA" },
    { key: "medicalAllowance", label: "Medical allowance" },
    { key: "statutoryBonus", label: "Statutory bonus" },
    { key: "specialAllowance", label: "Special allowance" },
    { key: "mobileInternetAllowance", label: "Mobile/Internet allowance" },
    { key: "medicalInsurance", label: "Medical insurance" },
    { key: "released_performance_bonus", label: "Performance bonus" },
    { key: "total_reimbursement", label: "Reimbursement allowance" },
    { key: "otherAllowance", label: "Other allowance" },
    { key: "totalPay", label: "Gross salary" }, // Keep at end
  ];

  const deductionsFields = [
    { key: "provident_fund", label: "Provident fund" },
    { key: "performance_bonus", label: "Performance bonus" },
    { key: "gratuity", label: "Gratuity" },
    { key: "other_deduction", label: "Other deduction" },
    { key: "total_deduction", label: "Total deduction" }, // Keep at end
  ];

  // Filter visible fields
  const getVisibleEarnings = () => {
    // Return all earnings fields except totalPay (will be added at end)
    return earningsFields.filter((field) => field.key !== "totalPay");
  };

  const getVisibleDeductions = () => {
    // Return all deductions fields except total_deduction (will be added at end)
    return deductionsFields.filter((field) => field.key !== "total_deduction");
  };

  // Create paired rows for display
  const createTableRows = () => {
    const visibleEarnings = getVisibleEarnings();
    const visibleDeductions = getVisibleDeductions();
    const maxRows = Math.max(visibleEarnings.length, visibleDeductions.length);
    const rows = [];

    for (let i = 0; i < maxRows; i++) {
      const earning = visibleEarnings[i];
      const deduction = visibleDeductions[i];

      rows.push(
        <tr key={`row-${i}`}>
          <td>{earning?.label || ""}</td>
          <td>
            <strong className="text-capitalize">
              {earning &&
              formData[earning.key] &&
              parseFloat(formData[earning.key]) > 0
                ? formatDecimal(formData[earning.key])
                : earning
                ? "-"
                : ""}
            </strong>
          </td>
          <td>{deduction?.label || ""}</td>
          <td>
            <strong className="text-capitalize">
              {deduction &&
              formData[deduction.key] &&
              parseFloat(formData[deduction.key]) > 0
                ? formatDecimal(formData[deduction.key])
                : deduction
                ? "-"
                : ""}
            </strong>
          </td>
        </tr>
      );
    }

    // Add the final row with Gross salary and Total deduction
    rows.push(
      <tr key="totals-row">
        <td>Gross salary</td>
        <td>
          <strong className="text-capitalize">
            {formData.totalPay ? formatDecimal(formData.totalPay) : "-"}
          </strong>
        </td>
        <td>Total deduction</td>
        <td>
          <strong className="text-capitalize">
            {formData.total_deduction
              ? formatDecimal(formData.total_deduction)
              : "-"}
          </strong>
        </td>
      </tr>
    );

    return rows;
  };

  return (
    <Modal show={show} centered size="lg">
      <Modal.Header className="d-flex justify-content-between ">
        <Modal.Title>Salary Details of {monthYear}</Modal.Title>
        {salaryId ? (
          <></>
        ) : (
          <Form.Group className="mb-0 d-flex justify-content-between">
            <Form.Control
              style={{ cursor: "pointer" }}
              id="monthInput"
              type="month"
              value={monthYear}
              onChange={handleMonthYearChange}
              max={getMaxDate()}
            />
          </Form.Group>
        )}
      </Modal.Header>
      {loading ? (
        <Spin
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "10px",
          }}
          size="large"
        />
      ) : error?.correctionPending ? (
        <Modal.Body>
          <p className="text-danger">
            {`* You have to wait until the ${
              companySalaryConfig?.payroll_release_month !== "same_month"
                ? new Date(
                    selectedMonthYear.year,
                    selectedMonthYear.month
                  ).toLocaleString("default", { month: "long" })
                : new Date(
                    selectedMonthYear.year,
                    selectedMonthYear.month - 1
                  ).toLocaleString("default", { month: "long" })
            } ${calculateSalaryGenerationDate}th for salary of ${new Date(
              selectedMonthYear.year,
              selectedMonthYear.month - 1
            ).toLocaleString("default", { month: "long" })}.`}
          </p>
        </Modal.Body>
      ) : (
        <Modal.Body>
          {salaryId || inEligibilityReason ? (
            <></>
          ) : (
            <div>
              <div
                className="d-flex  gap-2 justify-content-between"
                style={{ color: "black" }}
              >
                <p>
                  Company holidays: {employeeWorkingDetail?.total_holidays} Day
                </p>
                <p>
                  Leaves: {formatDecimal(employeeWorkingDetail?.total_leave)}{" "}
                  Day
                </p>
                <p>
                  Attendance:{" "}
                  {formatDecimal(employeeWorkingDetail?.total_attendance)} Day
                </p>
              </div>
              <div
                className="d-flex gap-2 justify-content-between"
                style={{ color: "black" }}
              >
                <p>
                  Employee worked:{" "}
                  {formatDecimal(employeeWorkingDetail?.salary_eligible_days)}{" "}
                  Day
                </p>
                <p>
                  Company working:{" "}
                  {monthYear &&
                    getDaysInMonth(monthYear) -
                      companyWorkingDays?.weekOffDatesCount}{" "}
                  Day
                </p>
              </div>
            </div>
          )}
          {(Number(employeeWorkingDetail?.total_attendance) ||
            Number(employeeWorkingDetail?.total_leave) ||
            salaryId) &&
          !inEligibilityReason ? (
            <Form>
              <Row>
                <Table bordered responsive>
                  <thead>
                    <tr style={{ textAlign: "center" }}>
                      <th
                        colSpan={2}
                        style={{
                          backgroundColor: companyThemeColor,
                          color: "white",
                          textAlign: "center",
                        }}
                      >
                        Earnings
                      </th>
                      <th
                        colSpan={2}
                        style={{
                          backgroundColor: companyThemeColor,
                          color: "white",
                          textAlign: "center",
                        }}
                      >
                        Deductions
                      </th>
                    </tr>
                  </thead>
                  <tbody>{createTableRows()}</tbody>
                  <tfoot>
                    <tr>
                      <th
                        colSpan={2}
                        style={{
                          backgroundColor: companyThemeColor,
                          color: "white",
                          textAlign: "center",
                        }}
                      >
                        Net Salary
                      </th>
                      <th
                        colSpan={2}
                        style={{
                          backgroundColor: companyThemeColor,
                          color: "white",
                          textAlign: "end",
                        }}
                      >
                        {formData?.net_salary
                          ? roundoffAmount(
                              formData?.net_salary -
                                roundoffAmount(formData?.other_deduction)
                            )
                          : "-"}
                      </th>
                    </tr>
                  </tfoot>
                </Table>

                {/* Input field for other_deduction */}
                <Col sm={6} lg={6} md={6} className="mt-3">
                  <Form.Group>
                    <Form.Label>
                      {" "}
                      <strong className="text-capitalize">
                        Other Deduction
                      </strong>
                    </Form.Label>
                    <Form.Control
                      id="other_deduction"
                      type="number"
                      min={0}
                      name="other_deduction"
                      value={formData.other_deduction}
                      onChange={handleInputChange}
                      placeholder="Enter deduction amount"
                    />
                    {error.other_deduction && (
                      <span className="text-danger">
                        * {error.other_deduction}
                      </span>
                    )}
                  </Form.Group>
                </Col>
                {formData.other_deduction > 0 ? (
                  <Col sm={6} lg={6} md={6} className="mt-3">
                    <Form.Group>
                      <Form.Label>
                        {" "}
                        <strong className="text-capitalize">
                          Deduction Reason{" "}
                          {formData.other_deduction > 0 ? (
                            <span className="text-danger">*</span>
                          ) : (
                            <></>
                          )}
                        </strong>
                      </Form.Label>
                      <Form.Control
                        id="deduction_reason"
                        as="textarea"
                        min={0}
                        name="other_deduction_reason"
                        value={formData.other_deduction_reason}
                        onChange={handleDeductionReason}
                        placeholder="Enter deduction reason"
                      />
                      {error.other_deduction_reason && (
                        <span className="text-danger">
                          * {error.other_deduction_reason}
                        </span>
                      )}
                    </Form.Group>
                  </Col>
                ) : (
                  <></>
                )}
              </Row>
            </Form>
          ) : (
            <p className="text-danger">
              *{" "}
              {inEligibilityReason
                ? inEligibilityReason
                : `Not eligible for 
              ${new Date(
                selectedMonthYear.year,
                selectedMonthYear.month - 1
              ).toLocaleString("default", { month: "long" })}
              salary.`}
            </p>
          )}
        </Modal.Body>
      )}
      <Modal.Footer>
        <Button
          variant="danger"
          onClick={() => {
            setFormData({
              other_deduction: 0,
              provident_fund: 0,
              performance_bonus: 0,
              net_salary: 0,
              totalPay: 0,
              total_deduction: 0,
              other_deduction_reason: "",
            });
            setMonthYear("");
            handleClose();
          }}
        >
          Cancel
        </Button>
        {!error?.correctionPending &&
        !inEligibilityReason &&
        (Number(employeeWorkingDetail?.total_attendance) ||
          Number(employeeWorkingDetail?.total_leave)) ? (
          <Button
            variant="primary"
            disabled={handleDisableGenerateButton()}
            onClick={handleGenerateButton}
          >
            Generate
          </Button>
        ) : (
          <></>
        )}
        {salaryId ? (
          <Button
            variant="primary"
            disabled={handleDisableGenerateButton()}
            onClick={() => {}}
          >
            Update
          </Button>
        ) : (
          <></>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SalaryDetail;
